import { Button } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, IconV3, RpiLink } from "@rpi/openapi-core";
import React from "react";
import { Body } from "../../../components/Typography";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { PublicUrl } from "../../../utils/link.util";
import { AccountSettingsCard } from "./AccountSettingsCard";
import { PAYMENT_METHOD_DESCRIPTION_PREFIX, PAYMENT_METHOD_TERMS_LINK_LABEL } from "./accountSettingsCopy";
import { FieldFrame } from "./FieldFrame";
import { Skeleton } from "./Skeleton";

interface PaymentMethodCardProps {
  customerPublicId: string;
}

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({ customerPublicId }) => {
  const navigateRoute = useNavigateRoute();
  const { data, isLoading } = GraphQL.useListPaymentMethodsQuery({ customerPublicId }, { enabled: !!customerPublicId });
  const paymentMethods = data?.listPaymentMethods;
  const savedCard = paymentMethods?.find((method) => method.isDefault) ?? paymentMethods?.[0];

  return (
    <AccountSettingsCard title="Payment Method" data-testid="payment-method-card">
      <Body>
        {PAYMENT_METHOD_DESCRIPTION_PREFIX}
        <RpiLink
          href={PublicUrl.TERMS_AND_CONDITIONS}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ font: "inherit", color: "inherit", fontWeight: 700 }}
        >
          {PAYMENT_METHOD_TERMS_LINK_LABEL}
        </RpiLink>
        .
      </Body>

      {isLoading ? (
        <FieldFrame data-testid="payment-method-loading">
          <Flex.Row justify="space-between" align="center" fullWidth>
            <Skeleton width={120} height={20} />
            <Skeleton width={80} height={20} />
          </Flex.Row>
        </FieldFrame>
      ) : savedCard ? (
        <FieldFrame>
          <Flex.Row justify="space-between" align="center" fullWidth>
            <Body>Credit Card</Body>
            <Flex.Row gap={16} align="center">
              {savedCard.imageUrl && <img src={savedCard.imageUrl} alt={savedCard.name ?? "Card"} height={20} />}
              <Body data-testid="payment-method-last-four">{`– ${savedCard.last4Digits}`}</Body>
              <Button
                variant="outlined"
                intent="neutral"
                size="small"
                aria-label="Edit payment method"
                iconRight={<IconV3 icon="edit" size={16} />}
                onClick={() => navigateRoute(["settings:edit-payment-method", { token: savedCard.token || "" }])}
              >
                Edit
              </Button>
            </Flex.Row>
          </Flex.Row>
        </FieldFrame>
      ) : (
        <Flex.Row>
          <Button
            variant="filled"
            intent="primary"
            size="small"
            onClick={() => navigateRoute(["settings:add-payment-method"])}
          >
            Add Payment Method
          </Button>
        </Flex.Row>
      )}
    </AccountSettingsCard>
  );
};
